import { plus } from '@0xtorch/big-decimal'
import { type CryptoCurrency, type FiatCurrency, rest } from '@0xtorch/core'
import { z } from 'zod'
import type { AssetBalance } from '../../types'
import { getAsset } from '../../utils/getAsset'
import { bitgetApiEndpoint, bitgetSymbolCryptoIdMap } from '../constants'
import { createPrivateGetHeaders } from '../utils'
import { spotAssetSchema } from './schema'

const responseSchema = z.object({
  code: z.string(),
  msg: z.string(),
  requestTime: z.number().int(),
  data: z.array(spotAssetSchema),
})

const urlPath = '/api/v2/spot/account/assets'

type FetchSpotAccountAssetsParameters = {
  apiKey: string
  apiSecret: string
  password: string
  proxy?: string
  proxyHeaders?: Record<string, string>
  cryptoCurrencies: readonly CryptoCurrency[]
  fiatCurrencies: readonly FiatCurrency[]
}

export const fetchSpotAccountAssets = async ({
  apiKey,
  apiSecret,
  password,
  proxy,
  proxyHeaders,
  cryptoCurrencies,
  fiatCurrencies,
}: FetchSpotAccountAssetsParameters): Promise<readonly AssetBalance[]> => {
  const timestamp = Date.now()

  const url = new URL(urlPath, bitgetApiEndpoint)
  const response = await rest(
    proxy === undefined
      ? url.toString()
      : `${proxy}${encodeURIComponent(url.toString())}`,
    {
      fetchOptions: {
        method: 'GET',
        headers: {
          ...proxyHeaders,
          ...(await createPrivateGetHeaders({
            apiKey,
            apiSecret,
            password,
            urlPath: url.pathname,
            query: '',
          })),
        },
      },
      schema: responseSchema,
    },
  )

  const assetBalances: AssetBalance[] = []
  for (const assetData of response.data) {
    const asset = getAsset({
      symbol: assetData.coin,
      cryptoCurrencies,
      fiatCurrencies,
      symbolCryptoIdMap: bitgetSymbolCryptoIdMap,
    })
    if (asset === undefined) {
      continue
    }
    assetBalances.push({
      key: 'bitget_spot',
      cryptoId: asset.type === 'CryptoCurrency' ? asset.id : undefined,
      fiatId: asset.type === 'FiatCurrency' ? asset.id : undefined,
      amount: plus(
        plus(assetData.available, assetData.frozen),
        assetData.locked,
      ),
      timestamp,
    })
  }

  return assetBalances
}
